import { Page } from "../../../components/page-base";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { Status, StatusChip } from "../../../components/datagrid/StatusChip";
import QualityBar from "../../../components/datagrid/QualityBar";
import { ClickableTextCell } from "../../../components/datagrid/ClickableTextCell";
import { NameCell } from "../../../components/datagrid/NameCell";
import { iniciais } from "../../../components/datagrid/iniciais";
import {
  ChipButton,
  ChipGroupLabel,
  FilledButton,
  TonalButton,
  ToolbarDivider,
} from "../../../components/datagrid/ChipButton";
import { KpiStrip } from "../../../components/kpi-strip";
import { formatarNumero } from "../../../components/kpi-strip/formatar";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { toast } from "react-toastify";

import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import SearchIcon from "@mui/icons-material/Search";
import {
  ChipFilterConfig,
  FilterDrawer,
} from "../../../components/filter-drawer";
import { Container } from "./styles";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import SyncIcon from "@mui/icons-material/Sync";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EmailService } from "../services";
import useUserStore from "../../../store/user";


const statusDictionary: Record<string, { status: Status; label: string }> = {
  ACTIVE: { status: "success", label: "Ativo" },
  COMPLETED: { status: "success", label: "Concluído" },
  SUCCESS: { status: "success", label: "Concluído" },
  RUNNING: { status: "warning", label: "Em processamento" },
  PROCESSING: { status: "warning", label: "Processando" },
  PENDING: { status: "warning", label: "Pendente" },
  NOT_STARTED: { status: "warning", label: "Não iniciado" },
  COPY: { status: "warning", label: "Cópia" },
  COPIED: { status: "warning", label: "Cópia" },
  ERROR: { status: "disabled", label: "Erro" },
  FAILED: { status: "disabled", label: "Falha" },
  SUSPENDED: { status: "disabled", label: "Suspenso" },
  BLOCKED: { status: "disabled", label: "Bloqueado" },
  PARTIAL_FAILURE: { status: "disabled", label: "Falha parcial" },
  DUPLICATED: { status: "warning", label: "Duplicado" },
  FETCHING: { status: "warning", label: "Buscando perfis" },
  "AUTH-ERROR": { status: "danger", label: "Reconectar" },
};

const mapStatusToChip = (
  rawStatus?: string
): { status: Status; label: string } => {
  if (!rawStatus) {
    return { status: "null", label: "-" };
  }

  const normalized = rawStatus.toUpperCase();
  const mapped = statusDictionary[normalized];
  if (mapped) {
    return mapped;
  }

  return { status: "warning", label: rawStatus };
};

export interface EmailAccount {
  id: number;
  email: string;
  connectedProfiles: number;
  quality: number;
  lastSync: string;
  status: Status;
  statusLabel: string;
  rawStatus?: string;
}

export interface ImportedProfileItem {
  id: number;
  displayName?: string;
  googleLocationId?: string;
  status?: string;
  reason?: string;
  processedAt?: string;
}

type ImportsManagerResponseItem = {
  id?: number;
  email?: string;
  totalItems?: number;
  processedItems?: number;
  status?: string;
  connectedProfiles?: number;
  quality?: number;
  lastSync?: string;
};

const initialFilters: { status: Status | "all" } = {
  status: "all",
};

export const EmailPage = () => {
  const { user } = useUserStore();
  const token = user?.token ?? "";
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isConnectingEmail, setIsConnectingEmail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  type SortDirection = "asc" | "desc" | null;
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [profilesDialog, setProfilesDialog] = useState<{
    open: boolean;
    loading: boolean;
    email: string;
    items: ImportedProfileItem[];
  }>({ open: false, loading: false, email: "", items: [] });

  const handleSortConnectedProfiles = useCallback(() => {
    setSortDirection((prev) =>
      prev === null ? "desc" : prev === "desc" ? "asc" : null
    );
  }, []);

  const fetchEmailAccounts = useCallback(async () => {
    if (!token) {
      return;
    }

    setIsLoadingAccounts(true);

    try {
      const response = await EmailService.importsManager(token, sortDirection ?? undefined);

      if (Array.isArray(response)) {
        // Filtra resposta para garantir que temos dados válidos
        const validResponse = response.filter(
          (item) => item && item.email && item.email !== "-"
        );

        const formatted = (validResponse as ImportsManagerResponseItem[]).map(
          (item): EmailAccount => {
            const { status, label } = mapStatusToChip(item?.status);
            return {
              id: item?.id ?? 0,
              email: item?.email ?? "-",
              connectedProfiles: item?.connectedProfiles ?? 0,
              quality: item?.quality ?? 0,
              lastSync: item?.lastSync ?? "Nunca",
              status,
              statusLabel: label,
              rawStatus: item?.status,
            };
          }
        );
        if (formatted.some((acc) => acc.rawStatus === "AUTH-ERROR")) {
          toast.warning(
            "Atenção: Algumas contas precisam ser reconectadas. Por favor, realizar a reconexão de email."
          );
        }
        console.log(formatted);
        setAccounts(formatted);
      } else {
        setAccounts([]);
      }
    } catch {
      // Error handling already done in service
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [token, sortDirection]);

  useEffect(() => {
    fetchEmailAccounts();
  }, [fetchEmailAccounts]);

  // Detecta se deve recarregar após OAuth
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("refresh") === "true") {
      // Remove o parâmetro da URL primeiro
      navigate("/email", { replace: true });

      // Aguarda um pequeno delay para garantir que o backend processou
      const timer = setTimeout(() => {
        fetchEmailAccounts();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [location.search, navigate, fetchEmailAccounts]);

  const handleConnectEmail = async () => {
    if (!user?.token) {
      toast.error("Por favor, realize login primeiro");
      return;
    }
    setIsConnectingEmail(true);
    try {
      await EmailService.oauthGoogle(user.token);
    } catch {
      setIsConnectingEmail(false);
    }
  };

  const filteredAccounts = useMemo(() => {
    let result = accounts;

    if (filters.status !== "all") {
      result = result.filter((acc) => acc.status === filters.status);
    }

    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter((acc) =>
        acc.email.toLowerCase().includes(lowerTerm)
      );
    }

    return result;
  }, [accounts, filters.status, searchTerm]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      totalCount: filteredAccounts.length,
      page: 1,
    }));
  }, [filteredAccounts.length]);

  const paginatedAccounts = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredAccounts.slice(startIndex, endIndex);
  }, [filteredAccounts, pagination.page, pagination.pageSize]);

  const reconectarCount = useMemo(
    () => accounts.filter((acc) => acc.status === "danger").length,
    [accounts],
  );
  const columns: DataGridColumn[] = useMemo(
    () => [
      { id: "email", header: "Conta", minWidth: 280 },
      { id: "status", header: "Status", minWidth: 130 },
      {
        id: "connectedProfiles",
        minWidth: 160,
        mobileLabel: "Perfis conectados",
      header: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Perfis Conectados
            <Tooltip
              title={
                sortDirection === null
                  ? "Ordenar decrescente"
                  : sortDirection === "desc"
                    ? "Ordenar crescente"
                    : "Remover ordenação"
              }
            >
              <IconButton size="small" onClick={handleSortConnectedProfiles}>
                {sortDirection === "desc" ? (
                  <ArrowDownwardIcon fontSize="inherit" />
                ) : sortDirection === "asc" ? (
                  <ArrowUpwardIcon fontSize="inherit" />
                ) : (
                  <UnfoldMoreIcon fontSize="inherit" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      { id: "quality", header: "Importação", minWidth: 150 },
      { id: "lastSync", header: "Última sincronização", minWidth: 170 },
      { id: "actions", header: "Ações", align: "right", minWidth: 220 },
    ],
    [sortDirection, handleSortConnectedProfiles]
  );
  function formatToBRDateTime(isoString: string): string {
    if (!isoString || isoString === "Nunca") return "Nunca";
    const parts = isoString.split("T");
    if (parts.length < 2) return isoString;

    const [datePart, timePart] = parts;
    const [year, month, day] = datePart.split("-");
    const [hour, minute] = timePart.split(":");

    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  const closeProfilesDialog = useCallback(() => {
    setProfilesDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const openProfilesDialog = useCallback(
    async (account: EmailAccount) => {
      setProfilesDialog({
        open: true,
        loading: true,
        email: account.email,
        items: [],
      });
      const data = await EmailService.getManagerProfiles(
        account.id.toString(),
        token,
      );
      setProfilesDialog({
        open: true,
        loading: false,
        email: account.email,
        items: Array.isArray(data) ? (data as ImportedProfileItem[]) : [],
      });
    },
    [token],
  );

  const resyncEmailAccount = useCallback(
    async (id: string) => {
      const result = await EmailService.resyncManager(id);
      await fetchEmailAccounts();
      if (result?.success) {
        // O backend processa de forma assíncrona; recarrega novamente em seguida.
        setTimeout(() => {
          fetchEmailAccounts();
        }, 4000);
      }
    },
    [fetchEmailAccounts],
  );

  const rows: DataGridRow[] = useMemo(
    () =>
      paginatedAccounts.map((acc) => ({
        id: acc.id,
        render: {
          email: (
            <NameCell
              primaryText={acc.email}
              avatar={iniciais(acc.email.replace(/[._-]/g, " ").replace(/@.*/, ""))}
              avatarTone={
                acc.status === "success" ? "green"
                : acc.status === "danger" ? "red"
                : acc.status === "disabled" ? "grey"
                : "yellow"
              }
            />
          ),
          status: <StatusChip status={acc.status} label={acc.statusLabel} />,
          connectedProfiles: (
            <ClickableTextCell
              text={String(acc.connectedProfiles)}
              onClick={() => openProfilesDialog(acc)}
            />
          ),
          quality: <QualityBar value={acc.quality} />,
          lastSync: (
            <Typography variant="body2" color="text.secondary">
              {formatToBRDateTime(acc.lastSync)}
            </Typography>
          ),
          actions: (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              {acc.status === "danger" && (
                <TonalButton
                  type="button"
                  style={{ background: "#FCE8E6", color: "#C5221F" }}
                  onClick={handleConnectEmail}
                  title="Conectar de novo esta conta no Google"
                >
                  <span className="material-icons">sync_problem</span>
                  Reconectar
                </TonalButton>
              )}
              <Tooltip title="Ver perfis desta conta" arrow>
                <IconButton size="small" onClick={() => openProfilesDialog(acc)} aria-label="Ver perfis desta conta">
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Resincronizar" arrow>
                <IconButton size="small" onClick={() => resyncEmailAccount(acc.id.toString())} aria-label="Resincronizar">
                  <SyncIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        },
      })),
    [paginatedAccounts, openProfilesDialog, resyncEmailAccount, handleConnectEmail]
  );

  const filterChips = useMemo<FilterChip[]>(() => {
    if (filters.status === "all") {
      return [];
    }

    const labelForStatus: Record<Status, string> = {
      success: "Ativo",
      disabled: "Suspenso",
      warning: "Cópia",
      info: "Aguardando",
      OK: "Ok",
      ATENCAO: "Atenção",
      CRITICO: "Crítico",
      null: "-",
      danger: "Reconectar",
      inactive: "Inativo",
      active: "Ativo",
    };

    return [
      {
        id: "status",
        label: `Status: ${labelForStatus[filters.status] ?? filters.status}`,
        onDelete: () => setFilters(initialFilters),
      },
    ];
  }, [filters.status]);

  const chipFilters: ChipFilterConfig[] = useMemo(
    () => [
      {
        id: "status",
        label: "Status",
        options: [
          { label: "Todos", value: "all" },
          { label: "Ativo", value: "success" },
          { label: "Suspenso", value: "disabled" },
          { label: "Cópia", value: "warning" },
          { label: "Reconectar", value: "danger" },
        ],
      },
    ],
    []
  );

  const handleDrawerFilterChange = useCallback(
    (field: string, value: Status | "all") => {
      if (field === "status") {
        setFilters({ status: value });
      }
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Integrações Google"
          subtitle="Contas Google que autorizam a GeoRanking a sincronizar e gerenciar perfis. Cada conta traz os perfis que administra."
          actions={
            <FilledButton type="button" disabled={isConnectingEmail} onClick={handleConnectEmail}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#fff" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.6 2.3 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z" />
                <path fill="#fff" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
                <path fill="#fff" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z" />
                <path fill="#fff" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.3 0-11.7-4.1-13.6-9.8l-7.8 6C6.5 42.6 14.6 48 24 48z" />
              </svg>
              {isConnectingEmail ? "Redirecionando..." : "Conectar conta Google"}
            </FilledButton>
          }
        />

        <Box
          sx={{
            width: "calc(100% - 64px)",
            pt: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {reconectarCount > 0 && (
            <Box
              role="alert"
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
                p: { xs: "16px", sm: "16px 22px" },
                borderRadius: "16px",
                background: "#FEF7E0",
                border: "1px solid #FDD663",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background: "#FDD663",
                  color: "#7A4B00",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <WarningIcon />
              </Box>
              <Box sx={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                <Typography sx={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 500, color: "#202124" }}>
                  Atenção: contas que precisam reconectar
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#3C4043" }}>
                  {reconectarCount} conta{reconectarCount > 1 ? "s tiveram" : " teve"} o token recusado pelo Google. Os perfis dela{reconectarCount > 1 ? "s" : ""} ficam sem sincronização até a reconexão.
                </Typography>
              </Box>
              <TonalButton
                type="button"
                style={{ background: "#fff", color: "#7A4B00" }}
                onClick={() => setFilters({ status: "danger" })}
              >
                Ver contas
                <span className="material-icons">arrow_forward</span>
              </TonalButton>
            </Box>
          )}
          <KpiStrip
            aria-label="Resumo das contas Google"
            items={[
              { icon: "mail", value: formatarNumero(accounts.length), label: "total", tone: "brand" },
              { icon: "check_circle", value: formatarNumero(accounts.filter((a) => a.status === "success").length), label: "ativos", tone: "green" },
              { icon: "sync_problem", value: formatarNumero(reconectarCount), label: "precisam reconectar", tone: "red" },
              { icon: "block", value: formatarNumero(accounts.filter((a) => a.status === "disabled").length), label: "suspensos ou com falha", tone: "grey" },
              { icon: "content_copy", value: formatarNumero(accounts.filter((a) => a.statusLabel === "Cópia" || a.statusLabel === "Duplicado").length), label: "cópia ou duplicado", tone: "yellow" },
            ]}
          />
          <AppDataGrid
            toolbarChips={
              <>
                <ToolbarDivider />
                <ChipGroupLabel>Status</ChipGroupLabel>
                {[
                  { label: "Todos", value: "all" as const, dot: null },
                  { label: "Ativo", value: "success" as const, dot: "#1E8E3E" },
                  { label: "Reconectar", value: "danger" as const, dot: "#D93025" },
                  { label: "Suspenso", value: "disabled" as const, dot: "#9AA0A6" },
                  { label: "Cópia", value: "warning" as const, dot: "#F9AB00" },
                ].map((opcao) => (
                  <ChipButton
                    key={opcao.value}
                    type="button"
                    $on={filters.status === opcao.value}
                    aria-pressed={filters.status === opcao.value}
                    onClick={() => setFilters({ status: opcao.value })}
                  >
                    {opcao.dot && <span className="dot" style={{ background: opcao.dot }} />}
                    {opcao.label}
                  </ChipButton>
                ))}
              </>
            }
            resultsLabel={`${formatarNumero(filteredAccounts.length)} de ${formatarNumero(accounts.length)} contas`}
            columns={columns}
            rows={rows}
            tableLabel="contas de e-mail"
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            filterChips={filterChips}
            filterCount={filterChips.length}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Nenhuma conta encontrada"
            noDataDescription="Ajuste sua busca ou adicione novas contas de e-mail."
            isLoading={isLoadingAccounts}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar por e-mail..."
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleDrawerFilterChange}
          onClearFilters={handleClearFilters}
          chipFilters={chipFilters}
        />
        <Dialog
          open={profilesDialog.open}
          onClose={closeProfilesDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Perfis do e-mail {profilesDialog.email}</DialogTitle>
          <DialogContent dividers>
            {profilesDialog.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : profilesDialog.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum perfil encontrado para este lote.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Perfil</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Motivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profilesDialog.items.map((item) => {
                    const chip = mapStatusToChip(item.status);
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          {item.displayName || item.googleLocationId || "-"}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={chip.status} label={chip.label} />
                        </TableCell>
                        <TableCell>{item.reason || "-"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeProfilesDialog}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page.Root>
  );
};
